import type { MouseEvent, ReactNode } from 'react';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * As ações discretas do inventário (#606) com aparência de link: sem fundo, sem moldura e sem a
 * altura de botão, sublinhadas no hover e no foco. O que não navega continua sendo um `<button>`;
 * o que navega é um `<a href>` de verdade.
 */
const APARENCIA_DE_LINK = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: '0.875rem',
  fontWeight: 600,
  lineHeight: 1.5,
  verticalAlign: 'middle',
  textAlign: 'left',
  textUnderlineOffset: '2px',
  '&:focus-visible': { textDecoration: 'underline', outline: '2px solid', outlineOffset: '2px' },
  '&:disabled': { color: 'text.disabled', cursor: 'default', textDecoration: 'none' },
  '& svg': { fontSize: 16 },
} as const;

type CorDoLink = 'primary.main' | 'error.main' | 'success.dark' | 'inherit';

interface AcaoEmLinkProps {
  children: ReactNode;
  onClick: (evento: MouseEvent<HTMLElement>) => void;
  cor?: CorDoLink;
  disabled?: boolean;
  'aria-label'?: string;
}

/** Ação que não navega: um `<button>` com cara de link. */
export function AcaoEmLink({ children, onClick, cor = 'primary.main', disabled = false, 'aria-label': rotulo }: AcaoEmLinkProps) {
  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      color={cor}
      onClick={onClick}
      disabled={disabled}
      aria-label={rotulo}
      sx={APARENCIA_DE_LINK}
    >
      {children}
    </Link>
  );
}

interface LinkDeNavegacaoProps {
  children: ReactNode;
  to: string;
}

/** Ação que navega: um `<a href>` de verdade, com a mesma aparência. */
export function LinkDeNavegacao({ children, to }: LinkDeNavegacaoProps) {
  return (
    <Link component={RouterLink} to={to} underline="hover" sx={APARENCIA_DE_LINK}>
      {children}
    </Link>
  );
}
